import { AgGridReact } from "ag-grid-react"
import React, { ForwardedRef, useCallback, useEffect, useMemo, useState } from "react"
import { useResizeDetector } from "react-resize-detector"
import { FlexContainer } from "../../../../../styled/flexbox.styled"
import { SmallText } from "../../../../../styled/text.styled"
import { ButtonGroup } from "./ButtonGroup"
import { ExposureGridContainer, ExposureGridHeader } from "./ExposureGrid.styled"

type ExposuresGridProps = React.ComponentProps<typeof AgGridReact> & {
  columns?: any[] // To detect display columns
}

const ExposureGrid = React.forwardRef(
  (props: ExposuresGridProps, ref: ForwardedRef<AgGridReact>) => {
    const [showDisplayColumns, setShowDisplayColumns] = useState(false)
    const { width, ref: resizeDectectorRef } = useResizeDetector()

    // Detect if we have display columns available
    const hasDisplayColumns = useMemo(() => {
      return (
        props.columnDefs?.some(col => "field" in col && col.field?.endsWith("_display")) || false
      )
    }, [props.columnDefs])

    // Extract columns and other props to avoid sending them to ag-grid
    const { columns: _, ...agGridProps } = props

    const sizeColumnsToFit = useCallback(() => {
      ;(ref as React.RefObject<AgGridReact>).current?.api?.sizeColumnsToFit()
    }, [ref])

    // Update column visibility when toggle changes
    const processedColumnDefs = useMemo(() => {
      if (!props.columnDefs || !hasDisplayColumns) {
        return props.columnDefs
      }

      return props.columnDefs.map(colDef => {
        const fieldName = "field" in colDef ? colDef.field : null
        const isCodeColumn = fieldName?.startsWith("B_") && !fieldName.endsWith("_display")
        const isDisplayColumn = fieldName?.startsWith("B_") && fieldName.endsWith("_display")

        if (isCodeColumn) {
          return { ...colDef, hide: showDisplayColumns }
        }

        if (isDisplayColumn) {
          return { ...colDef, hide: !showDisplayColumns }
        }

        return colDef
      })
    }, [props.columnDefs, showDisplayColumns, hasDisplayColumns])

    /**
     * Every time we resize the grid, we change
     * column size to fit the actual width.
     */
    useEffect(() => {
      sizeColumnsToFit()
    }, [width, sizeColumnsToFit])

    const toggleOptions = [
      { label: "Codes", value: "codes" },
      { label: "Names", value: "names" },
    ]

    return (
      <ExposureGridContainer ref={resizeDectectorRef} flexDirection="column">
        {hasDisplayColumns && (
          <ExposureGridHeader
            flexDirection="row"
            justifyContent="flex-end"
            alignItems="center"
            gap="10px"
          >
            <FlexContainer flexDirection="column" alignItems="center">
              <ButtonGroup
                handleChange={(value: string) => setShowDisplayColumns(value === "names")}
                options={toggleOptions}
                selected={showDisplayColumns ? "names" : "codes"}
              />
            </FlexContainer>
          </ExposureGridHeader>
        )}

        <div className="ag-theme-balham-dark">
          <AgGridReact ref={ref} {...agGridProps} columnDefs={processedColumnDefs} />
        </div>
      </ExposureGridContainer>
    )
  },
)

export default ExposureGrid
